import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { FieldTypeModel, IFieldType } from '../models/field-type.model';
import { IServerResponse } from '../models/server-response.model';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';

/** Body de `POST /field-types`. `fieldType` es la PK natural. */
export interface IFieldTypeCreateRequest {
  fieldType: string;
  description: string;
  protected: boolean;
  multiline: boolean;
}

/** Body de `PUT /field-types/{fieldType}`. La PK NO se actualiza. */
export interface IFieldTypeUpdateRequest {
  description: string;
  protected: boolean;
  multiline: boolean;
  status: boolean;
}

/**
 * Catalogo de tipos de campo.
 *
 * El listado no tiene paginacion. El API lo entrega ya ordenado por
 * `description asc` (la etiqueta visible, en español), asi que el cliente
 * lo consume tal cual llega: no reordena nada.
 * Los GET usan el `loading` local (skeletons); las mutaciones, el spinner
 * global. Los mensajes del envelope se muestran tal cual: los 409 / 400 del
 * API traen texto util que no conviene sustituir por un generico.
 */
@Injectable({ providedIn: 'root' })
export class FieldTypeService {
  private readonly http = inject(HttpClient);
  private readonly spinner = inject(SpinnerService);
  private readonly toast = inject(ToastService);
  private readonly util = inject(UtilService);
  private readonly urlApi = `${environment.urlApi}/field-types`;

  readonly loading = signal(false);
  readonly fieldTypes = signal<FieldTypeModel[]>([]);

  /**
   * `false` mientras NO se sabe todavia si hay datos: la primera peticion no ha
   * terminado. La vista pinta esqueletos hasta que pase a `true`; sin esta
   * senal el primer render enseñaria el estado vacio con la lista aun cargando.
   */
  readonly fieldTypesLoaded = signal(false);

  /** Mensaje del ultimo fallo de carga, o `null` si la carga salio bien. */
  readonly fieldTypesError = signal<string | null>(null);

  /** `status` filtra por activos / inactivos; omitirlo trae todos. */
  async getFieldTypes(status: boolean | null = null): Promise<FieldTypeModel[]> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ fieldTypes: IFieldType[] }>>(this.urlApi, {
          params: this.util.buildParams({ status }),
        }),
      );

      if (response.statusCode !== 200) {
        this.fieldTypesError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const fieldTypes = (response.data?.fieldTypes ?? []).map(
        (item) => new FieldTypeModel(item),
      );
      this.fieldTypes.set(fieldTypes);
      this.fieldTypesError.set(null);
      return fieldTypes;
    } catch (error) {
      const message = this.util.errorMessage(
        error,
        'No se pudieron cargar los tipos de campo.',
      );
      this.fieldTypesError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loading.set(false);
      this.fieldTypesLoaded.set(true);
    }
  }

  /** POST devuelve 201. Un 409 significa que la PK ya existe. */
  async createFieldType(request: IFieldTypeCreateRequest): Promise<FieldTypeModel | null> {
    this.spinner.show('Creando tipo de campo...');
    try {
      const response = await firstValueFrom(
        this.http.post<IServerResponse<{ fieldType: IFieldType }>>(this.urlApi, request),
      );

      if (response.statusCode !== 201) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.fieldType ? new FieldTypeModel(response.data.fieldType) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo crear el tipo de campo.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  async updateFieldType(
    fieldType: string,
    request: IFieldTypeUpdateRequest,
  ): Promise<FieldTypeModel | null> {
    this.spinner.show('Guardando tipo de campo...');
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ fieldType: IFieldType }>>(
          `${this.urlApi}/${encodeURIComponent(fieldType)}`,
          request,
        ),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.fieldType ? new FieldTypeModel(response.data.fieldType) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo guardar el tipo de campo.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  /** DELETE es soft-delete: deja `status = false`, no borra la fila. */
  async deleteFieldType(fieldType: string): Promise<FieldTypeModel | null> {
    this.spinner.show('Desactivando tipo de campo...');
    try {
      const response = await firstValueFrom(
        this.http.delete<IServerResponse<{ fieldType: IFieldType }>>(
          `${this.urlApi}/${encodeURIComponent(fieldType)}`,
        ),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.fieldType ? new FieldTypeModel(response.data.fieldType) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo desactivar el tipo de campo.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }
}
